import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Layout } from "@/components/Layout";

// Lazy load all pages for code splitting to reduce initial JavaScript payload
const Index = lazy(() => import("./pages/Index"));
const About = lazy(() => import("./pages/About"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const SellYourHouse = lazy(() => import("./pages/SellYourHouse"));
const AreasWeServe = lazy(() => import("./pages/AreasWeServe"));
const Reviews = lazy(() => import("./pages/Reviews"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const BlogHub = lazy(() => import("./pages/BlogHub"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const Situations = lazy(() => import("./pages/Situations"));
const Foreclosure = lazy(() => import("./pages/situations/Foreclosure"));
const Probate = lazy(() => import("./pages/situations/Probate"));
const Divorce = lazy(() => import("./pages/situations/Divorce"));
const TiredLandlords = lazy(() => import("./pages/situations/TiredLandlords"));
const ExtensiveRepairs = lazy(() => import("./pages/situations/ExtensiveRepairs"));
const Relocation = lazy(() => import("./pages/situations/Relocation"));
const FinancialHardship = lazy(() => import("./pages/situations/FinancialHardship"));
const Downsizing = lazy(() => import("./pages/situations/Downsizing"));

// Georgia
const AtlantaGA = lazy(() => import("./pages/cities/AtlantaGA"));
const MariettaGA = lazy(() => import("./pages/cities/MariettaGA"));
const AlpharettaGA = lazy(() => import("./pages/cities/AlpharettaGA"));
const DecaturGA = lazy(() => import("./pages/cities/DecaturGA"));
const SmyrnaGA = lazy(() => import("./pages/cities/SmyrnaGA"));
const RoswellGA = lazy(() => import("./pages/cities/RoswellGA"));
const LawrencevilleGA = lazy(() => import("./pages/cities/LawrencevilleGA"));

// North Carolina
const CharlotteNC = lazy(() => import("./pages/cities/CharlotteNC"));
const RaleighNC = lazy(() => import("./pages/cities/RaleighNC"));
const DurhamNC = lazy(() => import("./pages/cities/DurhamNC"));
const GreensboroNC = lazy(() => import("./pages/cities/GreensboroNC"));
const WinstonSalemNC = lazy(() => import("./pages/cities/WinstonSalemNC"));
const CaryNC = lazy(() => import("./pages/cities/CaryNC"));
const ConcordNC = lazy(() => import("./pages/cities/ConcordNC"));

// Florida
const OrlandoFL = lazy(() => import("./pages/cities/OrlandoFL"));
const WinterParkFL = lazy(() => import("./pages/cities/WinterParkFL"));
const SanfordFL = lazy(() => import("./pages/cities/SanfordFL"));
const TampaFL = lazy(() => import("./pages/cities/TampaFL"));
const MiamiFL = lazy(() => import("./pages/cities/MiamiFL"));
const JacksonvilleFL = lazy(() => import("./pages/cities/JacksonvilleFL"));
const StPetersburgFL = lazy(() => import("./pages/cities/StPetersburgFL"));
const HialeahFL = lazy(() => import("./pages/cities/HialeahFL"));
const LakelandFL = lazy(() => import("./pages/cities/LakelandFL"));
const KissimmeeFL = lazy(() => import("./pages/cities/KissimmeeFL"));
const DeltonaFL = lazy(() => import("./pages/cities/DeltonaFL"));
const DaytonaBeachFL = lazy(() => import("./pages/cities/DaytonaBeachFL"));
const TallahasseeFL = lazy(() => import("./pages/cities/TallahasseeFL"));

// Texas
const DallasTX = lazy(() => import("./pages/cities/DallasTX"));
const FortWorthTX = lazy(() => import("./pages/cities/FortWorthTX"));
const ArlingtonTX = lazy(() => import("./pages/cities/ArlingtonTX"));
const PlanoTX = lazy(() => import("./pages/cities/PlanoTX"));
const IrvingTX = lazy(() => import("./pages/cities/IrvingTX"));
const GarlandTX = lazy(() => import("./pages/cities/GarlandTX"));
const FriscoTX = lazy(() => import("./pages/cities/FriscoTX"));
const SanAntonioTX = lazy(() => import("./pages/cities/SanAntonioTX"));
const DFWTX = lazy(() => import("./pages/cities/DFWTX"));
const AustinTX = lazy(() => import("./pages/cities/AustinTX"));
const CorpusChristiTX = lazy(() => import("./pages/cities/CorpusChristiTX"));

const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>}>
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<Index />} />
                <Route path="/about" element={<About />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/sell-your-house" element={<SellYourHouse />} />
                <Route path="/areas-we-serve" element={<AreasWeServe />} />
                <Route path="/reviews" element={<Reviews />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/situations" element={<Situations />} />
                <Route path="/situations/foreclosure" element={<Foreclosure />} />
                <Route path="/situations/probate" element={<Probate />} />
                <Route path="/situations/divorce" element={<Divorce />} />
                <Route path="/situations/tired-landlords" element={<TiredLandlords />} />
                <Route path="/situations/extensive-repairs" element={<ExtensiveRepairs />} />
                <Route path="/situations/relocation" element={<Relocation />} />
                <Route path="/situations/financial-hardship" element={<FinancialHardship />} />
                <Route path="/situations/downsizing" element={<Downsizing />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<BlogHub />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                
                {/* Georgia */}
                <Route path="/sell-my-house-fast-atlanta-ga" element={<AtlantaGA />} />
                <Route path="/sell-my-house-fast-marietta-ga" element={<MariettaGA />} />
                <Route path="/sell-my-house-fast-alpharetta-ga" element={<AlpharettaGA />} />
                <Route path="/sell-my-house-fast-decatur-ga" element={<DecaturGA />} />
                <Route path="/sell-my-house-fast-smyrna-ga" element={<SmyrnaGA />} />
                <Route path="/sell-my-house-fast-roswell-ga" element={<RoswellGA />} />
                <Route path="/sell-my-house-fast-lawrenceville-ga" element={<LawrencevilleGA />} />

                {/* North Carolina */}
                <Route path="/sell-my-house-fast-charlotte-nc" element={<CharlotteNC />} />
                <Route path="/sell-my-house-fast-raleigh-nc" element={<RaleighNC />} />
                <Route path="/sell-my-house-fast-durham-nc" element={<DurhamNC />} />
                <Route path="/sell-my-house-fast-greensboro-nc" element={<GreensboroNC />} />
                <Route path="/sell-my-house-fast-winston-salem-nc" element={<WinstonSalemNC />} />
                <Route path="/sell-my-house-fast-cary-nc" element={<CaryNC />} />
                <Route path="/sell-my-house-fast-concord-nc" element={<ConcordNC />} />

                {/* Florida */}
                <Route path="/sell-my-house-fast-orlando-fl" element={<OrlandoFL />} />
                <Route path="/sell-my-house-fast-winter-park-fl" element={<WinterParkFL />} />
                <Route path="/sell-my-house-fast-sanford-fl" element={<SanfordFL />} />
                <Route path="/sell-my-house-fast-tampa-fl" element={<TampaFL />} />
                <Route path="/sell-my-house-fast-miami-fl" element={<MiamiFL />} />
                <Route path="/sell-my-house-fast-jacksonville-fl" element={<JacksonvilleFL />} />
                <Route path="/sell-my-house-fast-st-petersburg-fl" element={<StPetersburgFL />} />
                <Route path="/sell-my-house-fast-hialeah-fl" element={<HialeahFL />} />
                <Route path="/sell-my-house-fast-lakeland-fl" element={<LakelandFL />} />
                <Route path="/sell-my-house-fast-kissimmee-fl" element={<KissimmeeFL />} />
                <Route path="/sell-my-house-fast-deltona-fl" element={<DeltonaFL />} />
                <Route path="/sell-my-house-fast-daytona-beach-fl" element={<DaytonaBeachFL />} />
                <Route path="/sell-my-house-fast-tallahassee-fl" element={<TallahasseeFL />} />

                {/* Texas */}
                <Route path="/sell-my-house-fast-dallas-tx" element={<DallasTX />} />
                <Route path="/sell-my-house-fast-fort-worth-tx" element={<FortWorthTX />} />
                <Route path="/sell-my-house-fast-arlington-tx" element={<ArlingtonTX />} />
                <Route path="/sell-my-house-fast-plano-tx" element={<PlanoTX />} />
                <Route path="/sell-my-house-fast-irving-tx" element={<IrvingTX />} />
                <Route path="/sell-my-house-fast-garland-tx" element={<GarlandTX />} />
                <Route path="/sell-my-house-fast-frisco-tx" element={<FriscoTX />} />
                <Route path="/sell-my-house-fast-san-antonio-tx" element={<SanAntonioTX />} />
                <Route path="/sell-my-house-fast-dfw-tx" element={<DFWTX />} />
                <Route path="/sell-my-house-fast-austin-tx" element={<AustinTX />} />
                <Route path="/sell-my-house-fast-corpus-christi-tx" element={<CorpusChristiTX />} />
                
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
